import { Camera, Link2, FileText, Image as ImageIcon } from "lucide-react";
import { lerp, easeOut } from "./useFrame";

type Props = { frame: number };

/**
 * 0–4s: Four chaotic recipe sources fly in from different edges, slightly
 * rotated, then 4–6s they funnel toward the centre and shrink as the next
 * scene's phone frame takes over.
 */
export const SceneProblem = ({ frame }: Props) => {
  const sources = [
    { Icon: Camera, label: "Blurry photo", x: -180, y: -90, rot: -8, in: 6 },
    { Icon: Link2, label: "https://recipe...", x: 170, y: -100, rot: 6, in: 22 },
    { Icon: FileText, label: "Handwritten scrap", x: -200, y: 90, rot: 4, in: 38 },
    { Icon: ImageIcon, label: "Screenshot", x: 190, y: 100, rot: -6, in: 54 },
  ];

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Headline */}
      <div
        className="absolute top-6 left-1/2 -translate-x-1/2 text-center"
        style={{
          opacity: easeOut(lerp(frame, [0, 18], [0, 1])) * lerp(frame, [180, 220], [1, 0]),
          transform: `translate(-50%, ${lerp(frame, [0, 18], [-8, 0])}px)`,
        }}
      >
        <p className="font-display text-xl font-semibold text-foreground sm:text-2xl">
          Recipes everywhere.
        </p>
        <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
          Photos, links, screenshots, scraps…
        </p>
      </div>

      {sources.map(({ Icon, label, x, y, rot, in: inAt }, i) => {
        const enter = easeOut(lerp(frame, [inAt, inAt + 24], [0, 1]));
        // Funnel toward centre between frame 240–360
        const funnel = easeOut(lerp(frame, [240, 360], [0, 1]));
        const tx = x * (1 - funnel);
        const ty = y * (1 - funnel);
        const scale = (0.85 + 0.15 * enter) * (1 - 0.85 * funnel);
        const opacity = enter * (1 - funnel);
        return (
          <div
            key={i}
            className="absolute flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 shadow-card"
            style={{
              transform: `translate(${tx}px, ${ty}px) rotate(${rot * (1 - funnel)}deg) scale(${scale})`,
              opacity,
            }}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <span className="text-xs font-medium text-foreground">{label}</span>
          </div>
        );
      })}
    </div>
  );
};
